<script setup lang="ts">
import { accountBalanceQuery } from '@mp/api/sdpMerchant/AccountController.js'
import { ArrowRight } from '@element-plus/icons-vue'
import { useToggle } from '@vueuse/core'
import { useQuery } from '@tanstack/vue-query'
import { yunFormat } from '@mp/utils'
import { useRouter } from 'vue-router'
import { useIsSp, usePermissions } from '@mp/hooks'
import { IconFont } from '@mp/component'

const [hiddenAmount, tooggleHidden] = useToggle(true)
const router = useRouter()
const { isAuthorized } = usePermissions()

const { isNotSp } = useIsSp()

// #查询账户余额
const { data: balanceData } = useQuery({
  queryKey: ['mch/account/balance/query'],
  queryFn: () => accountBalanceQuery(),
  select: data => data?.data,
})
</script>

<template>
  <section id="DASHBOARD_ACCOUNT_OVERVIEW" class="bg-white p-4 rounded-lg select-none">
    <div class="flex items-center mb-3">
      <label class="text-base text-n-800 font-medium flex items-center">
        账户总览
        <IconFont
          v-show="!hiddenAmount" type="icon-chakan" class="ml-1 cursor-default text-n-400"
          @click="tooggleHidden()"
        />
        <IconFont
          v-show="hiddenAmount" type="icon-preview-close" class="ml-1 cursor-default text-n-400"
          @click="tooggleHidden()"
        />

      </label>
      <el-button v-if="!isNotSp" id="DASHBOARD_ACCOUNT_OVERVIEW_VIEW_MORE" v-track link type="primary" class="ml-auto" @click="router.push('/statement/capitalFlow/accountDetails')">
        查看更多
        <el-icon class="ml-1 -mr-2">
          <ArrowRight />
        </el-icon>
      </el-button>
    </div>

    <label class="block mb-2 text-sm flex items-center text-n-400">账户余额
      <el-popover
        placement="top-start" title="账户余额" :width="280" trigger="hover"
        content="含商户基本户、待结算账户、待分账冻结账户、扫码预授权冻结户等所有账户的余额总和"
      >
        <template #reference>
          <IconFont type="icon-yiwen1" class="text-n-400 ml-1" />
        </template>
      </el-popover>
    </label>
    <b class="block leading-none text-2xl text-n-800 mb-3">
      {{ hiddenAmount ? '****' : `￥${yunFormat(balanceData?.balance)}` }}
    </b>
    <div class="grid grid-cols-2 gap-3">
      <div class="bg-n-10 p-4 text-sm rounded-md mt-3">
        <label class="block text-n-400 flex items-center">待结算余额
          <el-popover
            placement="top-start" title="待结算余额" :width="280" trigger="hover"
            content="尚未进行结算的交易款项，结算后会转入基本户余额"
          >
            <template #reference>
              <IconFont type="icon-yiwen1" class="text-n-400 ml-1" />
            </template>
          </el-popover>
        </label>
        <b class="default-amount"> {{ hiddenAmount ? '****' : `￥${yunFormat(balanceData?.settlementBalance)}` }}</b>
      </div>
      <div class="bg-n-10 p-4 text-sm rounded-md mt-3">
        <label class="block text-n-400 flex items-center">冻结余额

          <el-popover
            placement="top-start" title="冻结余额" :width="280" trigger="hover"
            content="含商户基本户冻结余额、待结算账户冻结余额、待分账冻结账户余额、扫码预授权冻结户余额的总和"
          >
            <template #reference>
              <IconFont type="icon-yiwen1" class="text-n-400 ml-1" />
            </template>
          </el-popover>
        </label>
        <b class="default-amount"> {{ hiddenAmount ? '****' : `￥${yunFormat(balanceData?.frozenBalance)}` }}</b>
      </div>
    </div>
    <div class="bg-n-10 p-4 text-sm rounded-md mt-3">
      <div class=" flex justify-between items-center">
        <label class="block text-n-400 flex items-center">
          基本户可用余额
          <el-popover
            placement="top-start" title="基本户可用余额" :width="280" trigger="hover"
            content="用于日常经营活动的资金收付的余额，可用于退款、提现、转账等资金操作"
          >
            <template #reference>
              <IconFont type="icon-yiwen1" class="text-n-400 ml-1" />
            </template>
          </el-popover>
        </label>

        <template v-if="!isNotSp">
          <el-button
            v-if="isAuthorized('DEPOSIT')" id="DASHBOARD_ACCOUNT_OVERVIEW_DEPOSIT"
            v-track link type="primary" class="ml-auto"
            @click="router.push({ name: 'RechargeIndex' })"
          >
            充值
          </el-button>

          <template v-if="isAuthorized('WITHDRAW')">
            <el-divider direction="vertical" />
            <el-button id="DASHBOARD_ACCOUNT_OVERVIEW_WITHDRAW" v-track link type="primary" @click="router.push('/capital/withdrawMgt/home')">
              提现
            </el-button>
          </template>

          <template v-if="isAuthorized(['TRANSFER_TO_BANK', 'TRANSFER_TO_ACCOUNT'])">
            <el-divider direction="vertical" />
            <el-button id="DASHBOARD_ACCOUNT_OVERVIEW_TRANSFER" v-track link account-overview="primary" @click="router.push('/capital/payment/bank')">
              转账
            </el-button>
          </template>
        </template>
      </div>

      <b class="default-amount"> {{ hiddenAmount ? '****' : `￥${yunFormat(balanceData?.availableBalance)}` }}</b>
    </div>
  </section>
</template>

<style scoped>
.default-amount {
  @apply text-base text-black leading-none mt-2 block;
}
</style>
